import { Col, InputNumber, Row, Select, Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { useZonePropertyAnchor } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import {
  getEffectiveZoneArea,
  getEffectiveZoneHeight,
  ZoneParams,
} from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { RotateCcw } from 'lucide-react'
import { FC } from 'react'
import styled, { useTheme } from 'styled-components'

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin: 0 0 16px 0;
`

const SectionTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin: 16px 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const MATERIAL_OPTIONS = [
  { value: 'CS', label: 'Carbon Steel (CS)' },
  { value: 'SS304', label: 'Stainless Steel 304' },
  { value: 'SS316', label: 'Stainless Steel 316' },
  { value: 'CRA', label: 'Corrosion Resistant Alloy (CRA)' },
  { value: 'CRA_CLAD', label: 'CRA Clad' },
  { value: 'DUPLEX', label: 'Duplex Stainless Steel' },
  { value: 'INCONEL', label: 'Inconel' },
  { value: 'MONEL', label: 'Monel' },
  { value: 'TITANIUM', label: 'Titanium' },
  { value: 'OTHER', label: 'Other' },
]

const GEOMETRY_OPTIONS = [
  { value: 'RECTANGLE', label: 'Rectangle' },
  { value: 'CYLINDRICAL', label: 'Cylindrical' },
  { value: 'SPHERICAL', label: 'Spherical' },
  { value: 'CONICAL', label: 'Conical' },
  { value: 'ELLIPSOIDAL', label: 'Ellipsoidal' },
  { value: 'CUSTOM', label: 'Custom' },
]

export interface DesignFunctionPanelProps {
  zone: ZoneParams
  onChange: (updates: Partial<ZoneParams>) => void
  readonly?: boolean
}

export const DesignFunctionPanel: FC<DesignFunctionPanelProps> = ({
  zone,
  onChange,
  readonly,
}) => {
  const anchor = useZonePropertyAnchor(zone.id)
  const theme = useTheme()

  return (
    <Panel>
      <HelpText>
        Define the physical and design parameters for this zone. These
        parameters inform corrosion risk assessment, inspection technique
        selection, and deployment methods.
      </HelpText>

      <SectionTitle>Material & Geometry</SectionTitle>

      <Row gutter={16}>
        <Col span={12}>
          <CommentIndicator anchor={anchor('material')}>
            <FieldGroup>
              <Label>Zone Material</Label>
              <Select
                value={zone.material}
                onChange={(value) => onChange({ material: value })}
                placeholder='Select material'
                options={MATERIAL_OPTIONS}
                allowClear
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={12}>
          <CommentIndicator anchor={anchor('geometry')}>
            <FieldGroup>
              <Label>Zone Geometry</Label>
              <Select
                value={zone.geometry}
                onChange={(value) => onChange({ geometry: value })}
                placeholder='Select geometry'
                options={GEOMETRY_OPTIONS}
                allowClear
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <SectionTitle>Zone Dimensions</SectionTitle>

      <Row gutter={16}>
        <Col span={12}>
          <CommentIndicator anchor={anchor('height_mm')}>
            <FieldGroup>
              <Label>
                Zone Height or Length (mm)
                {!readonly && zone.height_mm_override && (
                  <Tag
                    color='orange'
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                    icon={<RotateCcw size={12} />}
                    onClick={() => onChange({ height_mm_override: undefined })}>
                    Reset
                  </Tag>
                )}
              </Label>
              <InputNumber
                value={getEffectiveZoneHeight(zone)}
                onChange={(value) => {
                  const override =
                    value !== zone.calculated_height_mm
                      ? (value ?? undefined)
                      : undefined
                  onChange({ height_mm_override: override })
                }}
                placeholder='Auto-calculated from bounds'
                style={{ width: '100%' }}
                min={0}
                disabled={readonly}
              />
              {zone.calculated_height_mm && (
                <span
                  style={{ fontSize: 11, color: theme.forecast.tertiaryText }}>
                  Calculated: {zone.calculated_height_mm.toFixed(2)} mm
                  {zone.height_mm_override && ' (Overridden)'}
                </span>
              )}
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={12}>
          <CommentIndicator anchor={anchor('external_diameter_mm')}>
            <FieldGroup>
              <Label>Zone Outer Diameter (OD) (mm)</Label>
              <InputNumber
                value={zone.external_diameter_mm}
                onChange={(value) =>
                  onChange({ external_diameter_mm: value ?? undefined })
                }
                placeholder='Enter outer diameter'
                style={{ width: '100%' }}
                min={0}
                step={1}
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <CommentIndicator anchor={anchor('total_surface_area_sq_m')}>
            <FieldGroup>
              <Label>
                Zone Total Surface Area (m²)
                {!readonly && zone.total_surface_area_sq_m_override && (
                  <Tag
                    color='orange'
                    style={{ marginLeft: 8, cursor: 'pointer' }}
                    icon={<RotateCcw size={12} />}
                    onClick={() =>
                      onChange({ total_surface_area_sq_m_override: undefined })
                    }>
                    Reset to Calculated
                  </Tag>
                )}
              </Label>
              <InputNumber
                value={getEffectiveZoneArea(zone)}
                onChange={(value) => {
                  const override =
                    value !== zone.calculated_area_sq_m
                      ? (value ?? undefined)
                      : undefined
                  onChange({ total_surface_area_sq_m_override: override })
                }}
                placeholder='Auto-calculated from bounds'
                style={{ width: '100%' }}
                min={0}
                step={0.01}
                disabled={readonly}
              />
              {zone.calculated_area_sq_m && (
                <span
                  style={{ fontSize: 11, color: theme.forecast.tertiaryText }}>
                  Calculated from bounds: {zone.calculated_area_sq_m.toFixed(2)}{' '}
                  m²
                  {zone.total_surface_area_sq_m_override && ' (Overridden)'}
                </span>
              )}
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <SectionTitle>Zone Wall Thickness & Corrosion Allowance</SectionTitle>

      <Row gutter={16}>
        <Col span={8}>
          <CommentIndicator anchor={anchor('wall_thickness_shell_mm')}>
            <FieldGroup>
              <Label>Zone Wall Thickness - Shell (mm)</Label>
              <InputNumber
                value={zone.wall_thickness_shell_mm}
                onChange={(value) =>
                  onChange({ wall_thickness_shell_mm: value ?? undefined })
                }
                placeholder='Zone Shell thickness'
                style={{ width: '100%' }}
                min={0}
                step={0.1}
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={8}>
          <CommentIndicator anchor={anchor('wall_thickness_heads_mm')}>
            <FieldGroup>
              <Label>Zone Wall Thickness - Heads (mm)</Label>
              <InputNumber
                value={zone.wall_thickness_heads_mm}
                onChange={(value) =>
                  onChange({ wall_thickness_heads_mm: value ?? undefined })
                }
                placeholder='Zone Head thickness'
                style={{ width: '100%' }}
                min={0}
                step={0.1}
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
        <Col span={8}>
          <CommentIndicator anchor={anchor('corrosion_allowance_mm')}>
            <FieldGroup>
              <Label>Zone Corrosion Allowance (mm)</Label>
              <InputNumber
                value={zone.corrosion_allowance_mm}
                onChange={(value) =>
                  onChange({ corrosion_allowance_mm: value ?? undefined })
                }
                placeholder='Zone CA'
                style={{ width: '100%' }}
                min={0}
                step={0.1}
                disabled={readonly}
              />
            </FieldGroup>
          </CommentIndicator>
        </Col>
      </Row>

      <div
        style={{
          marginTop: 16,
          padding: 12,
          background: theme.forecast.blueGlassBackground,
          borderRadius: 6,
          fontSize: 12,
          color: theme.forecast.secondaryText,
        }}>
        <strong>Informs:</strong> These parameters affect Corrosion Risk
        Assessment, Inspection Technique Selection, Deployment Method Selection,
        and Coverage Calculation.
      </div>
    </Panel>
  )
}
